import type { TranslationKeys } from './types';

export const es: TranslationKeys = {
    // Navbar
    navbar: {
      schools: 'Escuelas',
      howItWorks: 'Cómo Funciona',
      aboutMe: 'Sobre Mí',
      pricing: 'Precios',
      faqs: 'FAQs',
      memories: 'Recuerdos',
      myAccount: 'Mi cuenta',
    },
    // Home Page - HERO PRINCIPAL (H1, tagline, subtitle)
    home: {
      title: 'Encuentra tus fotos de surf en segundos',
      tagline: 'Fotografía de surf en Lanzarote',
      subtitle: 'Sube una foto y encuentra tus recuerdos al instante, sin buscar entre cientos de imágenes',
      cta: 'Encontrar Mis Fotos',
      stats: {
        photos: 'Fotos',
        schools: 'Escuelas',
        surfers: 'Surfistas Felices',
      },
    },
    // Schools Page - ESCUELAS
    schools: {
      title: 'Escuelas de Surf',
      subtitle: 'Selecciona tu escuela de surf para encontrar tus fotos',
      viewPhotos: 'Ver Fotos',
      introTitle: 'Tu escuela, tu día, tus recuerdos',
      descriptionMain: 'Selecciona la escuela con la que hiciste tu clase de surf y encuentra tu sesión organizada por fecha.',
      descriptionSecondary: 'Sesiones diarias de fotografía de surf en Famara y La Santa.',
      loading: 'Cargando escuelas...',
      apiUnavailable:
        'No se pudo conectar con el servidor (tiempo agotado o error). Mostrando la lista por defecto — vuelve a intentarlo en unos minutos.',
      retry: 'Reintentar',
      otherSchools: 'OTRAS ESCUELAS',
      valueHighQuality: 'Fotos en alta calidad',
      valueInstantDownload: 'Descarga inmediata',
      valueSecurePayment: 'Pago seguro',
    },
    // How It Works Page - ACCESO A FOTOS (SELFIE)
    howItWorks: {
      title: 'Encuentra tus fotos de forma segura',
      subtitle: 'Sube un selfie para que nuestro sistema identifique únicamente tus fotos de forma automática.',
      step1: {
        title: 'Elige tu escuela de surf',
        description: 'Selecciona la escuela donde hiciste tu clase',
      },
      step2: {
        title: 'Selecciona la fecha de tu sesión',
        description: 'Elige el día en el que surfeaste',
      },
      step3: {
        title: 'Sube un selfie',
        description: 'Nuestro sistema identificará únicamente tus fotos de forma automática',
      },
      step4: {
        title: 'Visualiza tus fotos',
        description: 'Ve todas las fotos en las que apareces',
      },
      step5: {
        title: 'Añade tus recuerdos',
        description: 'Selecciona las fotos que deseas guardar',
      },
      step6: {
        title: 'Paga de forma segura',
        description: 'Realiza el pago de manera rápida y segura',
      },
      step7: {
        title: 'Descarga inmediata',
        description: 'Obtén tus fotos al instante sin marca de agua',
      },
      smartSearch: {
        title: 'Búsqueda inteligente',
        description: 'Este proceso garantiza tu privacidad y evita que otras personas puedan acceder o comprar fotos que no les pertenecen.',
      },
      cta: 'Encuentra tus fotos',
    },
    // About Me Page - SOBRE MÍ
    aboutMe: {
      label: 'Sobre Mí',
      title: 'Capturando tus mejores',
      titleHighlight: 'momentos en el agua',
      intro: 'Soy <strong>Germán Abreu</strong>, fotógrafo especializado en surf y deportes acuáticos en Lanzarote. Vivo en La Santa y trabajo principalmente en Famara y en otros spots de surf de la isla.',
      paragraph1: 'Después de años fotografiando a surfistas y alumnos de escuelas, detecté siempre el mismo problema: encontrar tus fotos entre cientos de imágenes era lento y poco práctico.',
      paragraph2: 'Por eso nace <strong className="brand-name">Surf Photo Lanzarote</strong>, una forma sencilla, rápida y segura de que cada persona encuentre solo sus fotos, sin búsquedas interminables y respetando siempre la privacidad.',
      mission: 'Mi objetivo es que te lleves un recuerdo auténtico de tu experiencia en el agua. Porque las olas pasan… pero los recuerdos se quedan.',
      stats: {
        experience: '+8 años',
        experienceLabel: 'Experiencia',
        schools: '+7',
        schoolsLabel: 'Escuelas colaboradoras',
      },
    },
    // Pricing Page
    pricing: {
      title: 'Precios',
      subtitle: 'Elige la opción que mejor se adapte a ti',
      plan1: {
        title: 'Tu mejor recuerdo',
        description: 'Una imagen individual en alta calidad',
        details: [
          'Descarga inmediata',
          'Alta resolución',
          'Formato JPG',
          'Válido para impresión',
          'Sin marca de agua',
        ],
      },
      plan2: {
        title: 'Todos los recuerdos de tu sesión',
        description: 'Todas las fotos de una misma persona en un mismo día.',
        details: [
          'Todas las fotos del día',
          'Descarga inmediata',
          'Alta resolución',
          'Formato JPG',
          'Sin marca de agua',
        ],
      },
      plan3: {
        title: 'Día adicional',
        description: 'Añade otro día a tu pack.',
        details: [
          'Todas las fotos del día extra',
          'Descarga inmediata',
          'Alta resolución',
          'Sin marca de agua',
        ],
      },
      plan4: {
        title: 'Persona adicional',
        description: 'Añade las fotos de otra persona. Puede ser del mismo día o de un día diferente.',
        details: [
          'Todas las fotos de otra persona',
          'Descarga inmediata',
          'Alta resolución',
          'Sin marca de agua',
        ],
      },
      includes: 'Incluye',
      note: 'Todos los precios incluyen descarga inmediata sin marca de agua',
    },
    // FAQ Page
    faqs: {
      title: 'Preguntas Frecuentes',
      subtitle: 'Encuentra respuestas a las dudas más comunes sobre cómo funciona Surf Photo Lanzarote',
      contactText: '¿No encuentras la respuesta que buscas?',
      contactLink: 'Contáctanos',
      questions: [
        {
          q: '¿Cómo encuentro mis fotos?',
          a: 'Selecciona tu escuela de surf, el día de tu sesión y sube un selfie. La web te mostrará únicamente las fotos en las que apareces ese día.',
        },
        {
          q: '¿Por qué tengo que subir un selfie?',
          a: 'Utilizamos tecnología de reconocimiento facial para identificar tu rostro y mostrarte solo tus fotos, sin que tengas que buscarlas entre cientos de imágenes.',
        },
        {
          q: '¿Qué pasa con el selfie que subo?',
          a: 'El selfie se utiliza únicamente para realizar la búsqueda de ese día y no se almacena ni se utiliza con otros fines.',
        },
        {
          q: '¿Puedo buscar fotos de varios días con un solo selfie?',
          a: 'No. Cada búsqueda se realiza por día. Si surfeaste en días distintos, deberás subir un selfie en cada sesión para encontrar tus fotos.',
        },
        {
          q: '¿Qué hago si somos dos o más personas?',
          a: 'Cada persona debe subir su propio selfie para encontrar sus fotos. Puedes añadir las fotos de cada persona al carrito y continuar sin problema.',
        },
        {
          q: '¿Por qué las fotos tienen marca de agua?',
          a: 'Las fotos se muestran con marca de agua para proteger el trabajo del fotógrafo. Tras la compra, podrás descargarlas sin marca de agua.',
        },
        {
          q: '¿Qué calidad tienen las fotos descargadas?',
          a: 'Las fotos se descargan en alta calidad, listas para uso personal, redes sociales e impresión.',
        },
        {
          q: '¿Cuánto tiempo estarán disponibles mis fotos?',
          a: 'Las fotos permanecen online durante un tiempo limitado. Te recomendamos descargarlas lo antes posible tras la compra.',
        },
        {
          q: '¿Puedo comprar fotos de más de un día?',
          a: 'Sí. Puedes acceder a diferentes días y comprar las fotos de cada sesión de forma independiente.',
        },
        {
          q: '¿Las fotos son públicas?',
          a: 'No. Las fotos están organizadas por escuela y día para que cada cliente acceda únicamente a su sesión.',
        },
        {
          q: '¿Es seguro el pago?',
          a: 'Sí. Todos los pagos se realizan a través de Stripe, una plataforma de pago segura y fiable.',
        },
        {
          q: '¿Qué hago si no me encuentro o tengo un problema?',
          a: 'Si no apareces en las fotos o tienes cualquier incidencia, puedes contactar con nosotros y te ayudaremos.',
        },
      ],
    },
    // School Days Page
    schoolDays: {
      title: 'Selecciona un Día',
      subtitle: 'Elige el día de tu sesión de surf',
      noPhotos: 'No hay fotos disponibles para esta escuela todavía.',
      back: 'Volver',
      searchByDate: 'Buscar por fecha',
      search: 'Buscar',
      availableDays: 'Días disponibles',
      selectDaySubtitle: 'Selecciona un día para encontrar tus fotos',
      loadingDays: 'Cargando días disponibles...',
      noDaysYet: 'No hay días disponibles para esta escuela todavía.',
      noDaysDesc: 'Las fotos se organizan por día. Vuelve pronto para ver nuevas sesiones.',
      photos: 'fotos',
      errorLoadingDays: 'Error al cargar los días disponibles',
      noPhotosForDate: 'No hay fotos disponibles para esa fecha',
    },
    // Day Photos Page
    dayPhotos: {
      title: 'Tus Fotos de Surf',
      subtitle: 'Fotos del',
      findTitle: 'Encuentra tus fotos',
      findSubtitle: 'Elige si quieres subir un selfie o activar la cámara para que detectemos tu rostro.',
      clickToSelect: 'Haz clic para seleccionar un selfie',
      orDragImage: 'o arrastra una imagen aquí',
      faceGuide: {
        hintStarting: 'Preparando la guía facial…',
        hintNoFace: 'Coloca tu rostro dentro del óvalo',
        hintMultiple: 'Que aparezca solo una persona',
        hintTooFar: 'Acércate un poco',
        hintTooClose: 'Aléjate un poco',
        hintMoveLeft: 'Muévete un poco a la izquierda',
        hintMoveRight: 'Muévete un poco a la derecha',
        hintMoveUp: 'Sube un poco el rostro',
        hintMoveDown: 'Baja un poco el rostro',
        hintLookStraight: 'Mira de frente a la cámara',
        turnLeft: 'Gira la cabeza a la izquierda y mantén',
        turnRight: 'Ahora gira la cabeza a la derecha',
        circleHead: 'Haz un círculo lento con la cabeza',
        coverage: 'Rostro {{pct}}%',
        hintHoldStill: 'Perfecto, no te muevas…',
        hintReady: 'Capturado',
        cameraDenied: 'No hay permiso de cámara. Puedes subir un selfie o volver a intentar.',
        choosePrompt: '¿Cómo quieres que encontremos tu rostro?',
        uploadSelfie: 'Subir un selfie',
        uploadSelfieHint: 'Usa una foto que ya tengas',
        startCamera: 'Activar la cámara',
        startCameraHint: 'Te guiamos para detectar tu rostro',
        backToChoice: 'Elegir otra opción',
        capture: 'Capturar',
        useGallery: 'Subir de la galería',
        loadingDetector: 'Activando la cámara…',
        galleryMultipleFaces: 'Subí un selfie con una sola persona (sin grupo).',
        galleryNoFace: 'No se detectó un rostro en la imagen. Probá con otro selfie.',
      },
      changePhoto: 'Cambiar foto',
      analyzing: 'Analizando...',
      searchPhotos: 'Buscar mis fotos',
      errorReadFile: 'Error al leer el archivo. Por favor, intenta con otra imagen.',
      errorSelectFirst: 'Por favor, selecciona una foto primero',
      errorEmptyFile: 'El archivo seleccionado está vacío. Por favor, selecciona otra imagen.',
      errorAnalyze: 'Error al analizar la foto. Verifica que el servidor esté en marcha.',
      yourSelfie: 'Tu selfie',
      analyzingFace: 'Analizando tu rostro...',
      streamingMatchesSoFar: '{{count}} coincidencias por ahora — sigue buscando…',
      slowPathHint:
        'esta carpeta aún no está indexada, así que comparamos foto a foto (lento solo la primera vez).',
      autoIndexHint:
        'Estamos creando el índice en segundo plano — la próxima búsqueda en esta carpeta será mucho más rápida.',
      indexClustersHint:
        'Creando el índice de rostros (clusters)… solo ocurre una vez por escuela/día — luego la búsqueda usa solo clusters.',
      searchDiagnostics:
        'Búsqueda facial: {{path}} · modo {{mode}} · {{seconds}}s',
      searchingIn: 'Buscando coincidencias en',
      analysisResults: 'Resultados del Análisis',
      matches: 'Coincidencias',
      photosAnalyzed: 'Fotos Analizadas',
      percentage: 'Porcentaje',
      photosFound: 'Fotos Encontradas',
      addAllMemories: 'Añadir todas tus recuerdos',
      addAllMemoriesFor35: 'Añade todos tus recuerdos por {{price}}€',
      similarity: 'Similitud',
      buy: 'Comprar',
      viewLarge: 'Ver en grande',
      noMatch: 'Fotos sin Coincidencia',
      moreItems: 'más',
      uploadPhoto: 'Sube Tu Foto',
      uploadDescription: 'Sube una foto clara de tu rostro para encontrar tus fotos de surf',
      selectFile: 'Seleccionar Foto',
      findPhotos: 'Encontrar Mis Fotos',
      results: 'fotos encontradas',
      addToCart: 'Añadir al Carrito',
      noResults: 'No se encontraron fotos. Intenta subir una foto diferente.',
      savedSearchBanner: 'Mostrando tu búsqueda guardada — todas las fotos que encontraste.',
      newSearchLink: 'Hacer una nueva búsqueda con otro selfie',
      savedSearchLoading: 'Cargando búsqueda guardada...',
      savedSearchNotFound: 'No se encontró la búsqueda guardada. Puedes hacer una nueva búsqueda abajo.',
      partnerOffer: 'Oferta',
      partnerOfferCta: 'Ver oferta',
      partnerOffersWhileSearching: 'Ofertas de colaboradores mientras buscamos tus fotos',
    },
    // Checkout Success Page
    account: {
      loginTitle: 'Iniciar sesión',
      registerTitle: 'Crea tu perfil',
      loginLead: 'Accede a tus compras y búsquedas guardadas hasta 90 días.',
      continueGoogle: 'Continuar con Google',
      orEmail: 'o con email',
      displayName: 'Nombre',
      email: 'Email',
      password: 'Contraseña',
      passwordStrengthLabel: 'Seguridad de la contraseña',
      strengthWeak: 'Débil',
      strengthFair: 'Regular',
      strengthGood: 'Buena',
      strengthStrong: 'Fuerte',
      strengthHint: 'Usa mayúsculas, números y símbolos',
      signIn: 'Entrar',
      signUp: 'Crear cuenta',
      forgotPassword: '¿Olvidaste tu contraseña?',
      forgotPasswordTitle: 'Recuperar contraseña',
      forgotPasswordLead: 'Introduce tu email y te enviaremos un enlace para elegir una nueva contraseña.',
      sendResetLink: 'Enviar enlace',
      resetLinkSent: 'Si existe una cuenta con ese email, te enviamos un enlace de recuperación.',
      resetLinkError: 'No se pudo enviar el enlace. Inténtalo de nuevo.',
      backToSignIn: 'Volver a entrar',
      resetPasswordTitle: 'Nueva contraseña',
      resetPasswordLead: 'Elige una contraseña nueva para tu cuenta.',
      newPassword: 'Nueva contraseña',
      savePassword: 'Guardar contraseña',
      passwordResetSuccess: 'Contraseña actualizada. Ya puedes iniciar sesión.',
      passwordResetInvalid: 'El enlace caducó o no es válido.',
      passwordResetError: 'No se pudo actualizar la contraseña. Inténtalo de nuevo.',
      passwordMismatch: 'Las contraseñas no coinciden.',
      confirmPassword: 'Confirmar contraseña',
      noAccount: '¿No tienes cuenta?',
      haveAccount: '¿Ya tienes cuenta?',
      createAccount: 'Crear una',
      photographerCta: '¿Eres fotógrafo?',
      photographerRegister: 'Crear cuenta de fotógrafo',
      photographerPanel: 'Panel de fotógrafo',
      verifyEmailSent: 'Revisa tu correo para verificar la cuenta.',
      backHome: 'Volver al inicio',
      oauthLoading: 'Iniciando sesión...',
      oauthError: 'Error al iniciar sesión. Inténtalo de nuevo.',
      oauthMissing: 'Enlace de acceso no válido.',
      emailVerifiedTitle: 'Email verificado',
      emailVerifiedBody: 'Tu perfil está activo. Ya puedes entrar y ver tu galería.',
      goToAccount: 'Ir a mi cuenta',
      emailInvalidTitle: 'Enlace no válido',
      emailInvalidBody: 'El enlace de verificación expiró o no es válido.',
      checkEmailTitle: 'Revisa tu correo',
      checkEmailBody: 'Te enviamos un enlace para verificar tu cuenta.',
      checkEmailSpam: 'Si no lo ves, revisa spam o promociones.',
      resendVerification: 'Reenviar correo de verificación',
      resendSuccess: 'Si la cuenta existe, te enviamos un nuevo correo.',
      resendError: 'No se pudo reenviar el correo. Inténtalo de nuevo.',
      resendEmailRequired: 'Introduce tu email para reenviar.',
      emailNotConfigured:
        'No pudimos enviar el correo desde el servidor. Contacta con soporte o usa Google.',
      registerCreatedNoEmailTitle: 'Cuenta creada',
      registerCreatedNoEmailBody:
        'Tu cuenta se guardó correctamente, pero aún no pudimos enviar el correo de verificación. Usa reenviar abajo o entra con Google.',
      galleryTitle: 'Mi galería',
      profileSubtitle: 'Tus compras y búsquedas guardadas hasta 90 días después de cada sesión.',
      tabProfile: 'Perfil',
      hello: 'Hola',
      signOut: 'Cerrar sesión',
      tabPurchases: 'Compras',
      tabSearches: 'Búsquedas',
      statPurchases: 'Compras',
      statSearches: 'Búsquedas',
      statPhotos: 'Fotos',
      editProfile: 'Editar perfil',
      saveProfile: 'Guardar cambios',
      profileSaved: 'Perfil actualizado',
      profileSaveError: 'No se pudo guardar el perfil',
      changePhoto: 'Cambiar foto',
      removePhoto: 'Quitar foto',
      photoHint: 'JPG o PNG, máx. 5 MB. Se muestra en tu perfil.',
      photoUploadError: 'No se pudo subir la foto',
      photoRemoved: 'Foto eliminada',
      photoUploaded: 'Foto actualizada',
      emailReadonly: 'El email no se puede cambiar aquí',
      authGoogle: 'Cuenta Google',
      authEmail: 'Email y contraseña',
      verified: 'Verificado',
      photosInOrder: 'fotos',
      orderDate: 'Fecha del pedido',
      downloadAvailable: 'Descarga disponible',
      loadError: 'No se pudo cargar tu galería.',
      emailVerifyRequired:
        'Verifica tu email para acceder a tu galería, compras y descargas.',
      verifyEmailLink: 'Verificar email',
      noPurchases: 'Aún no tienes compras.',
      noSearches: 'No hay búsquedas guardadas.',
      order: 'Pedido',
      downloadZip: 'Descargar ZIP',
      expired: 'Periodo de descarga finalizado',
      newSearch: 'Buscar fotos de otro día',
      photosFound: 'fotos encontradas',
      viewSession: 'Ver sesión',
      searchSavedAt: 'Búsqueda guardada',
      loading: 'Cargando...',
    },
    profilePrompt: {
      beforePayTitle: 'Crea tu perfil',
      beforePayBody:
        'Inicia sesión con Google o email y podrás descargar tus fotos cuando quieras hasta 90 días después de la fecha de la sesión.',
      afterPayTitle: 'Guarda tus fotos en tu perfil',
      afterPayBody:
        'Sin perfil solo podrás descargarlas ahora. Con perfil accedes cuando quieras durante 90 días.',
      exitTitle: 'Guarda tus resultados',
      exitBody: 'Crea un perfil para no repetir el selfie la próxima vez.',
      createProfile: 'Crear perfil',
      saveToProfile: 'Guardar en mi perfil',
      saveResults: 'Guardar resultados',
      continueGoogle: 'Continuar con Google',
      notNow: 'Ahora no',
      downloadNowOnly: 'Solo descargar ahora',
    },
    checkoutSuccess: {
      loading: 'Cargando tus descargas...',
      paymentNotFound: 'No se encontró el pago',
      noDownloadData: 'No hay datos de descarga.',
      testHint: 'Para probar la sección de descargas sin pagar, visita:',
      backHome: 'Volver al inicio',
      title: '¡Pago completado!',
      subtitle: 'Gracias por tu compra. Aquí tienes tus fotografías para descargar sin marca de agua.',
      hello: 'Hola',
      downloads: 'Descargas',
      downloadsIntro: 'Puedes descargar cada foto en alta calidad haciendo clic en el botón o en la imagen.',
      downloadAll: 'Descargar todas las fotos',
      downloadingAll: 'Iniciando descarga…',
      downloadThis: 'Descargar esta foto',
      downloadingOne: 'Descargando…',
      downloadTitle: 'Descargar',
      total: 'Total',
      photo: 'foto',
      photos: 'fotos',
      exploreMore: 'Ver más escuelas',
      missingSession: 'Falta session_id o modo prueba (test=1).',
      paymentIncomplete:
        'No pudimos cargar los detalles ahora mismo, pero si ya completaste el pago, este se ha realizado correctamente — por favor NO vuelvas a pagar. Actualiza esta página en un momento, o contáctanos con tu email de compra y te enviaremos las fotos.',
      loadError: 'No se pudieron cargar los detalles del pago.',
      unexpectedResponse: 'Respuesta inesperada del servidor.',
      connectionError: 'Error de conexión. Comprueba que el backend esté en marcha.',
      downloadsOnThisPage:
        'Tus fotos están en esta página. Usa "Descargar todas las fotos" o cada botón "Descargar esta foto" de abajo.',
      guestProfileBenefit:
        'Puedes descargar tus fotos aquí cuando quieras con este enlace. Crea un perfil para guardarlas en tu galería y acceder cuando quieras.',
      createProfileCta: 'Crear perfil',
      goToGallery: 'Mi galería',
      downloadFailed: 'No se pudo descargar. Prueba "Descargar todas las fotos" o recarga esta página.',
      voucherEmail:
        'También te hemos enviado por email la confirmación de compra con los cupones de colaboradores.',
    },
    checkoutCancel: {
      title: 'Pago cancelado',
      message: 'No se ha realizado ningún cargo. Puedes volver al carrito o seguir navegando.',
      backHome: 'Volver al inicio',
      viewSchools: 'Ver escuelas',
    },
    // Cart Modal
    cart: {
      title: 'Tus Recuerdos',
      item: 'artículo',
      items: 'artículos',
      empty: 'Tu carrito está vacío',
      emptyDesc: 'Explora nuestras fotos y añade tus recuerdos favoritos',
      explorePhotos: 'Explorar Fotos',
      total: 'Total',
      checkout: 'Proceder al Pago',
      remove: 'Eliminar',
      beforePaymentDownloadNotice:
        'Tus fotos estarán disponibles para descargar en esta misma página inmediatamente después del pago',
      emailPlaceholder: 'Tu email',
      namePlaceholder: 'Tu nombre',
      subtotal: 'Subtotal',
      igic: 'IGIC ({rate}%)',
      taxesNotIncludedNote: 'Impuestos no incluidos en los precios',
      discount: 'Descuento',
      processing: 'Procesando...',
      proceedToPayment: 'Proceder al Pago',
      continueShopping: 'Seguir Comprando',
      securePayment: 'Pago Seguro',
      instantDownload: 'Descarga Instantánea',
      closeCart: 'Cerrar carrito',
      removeItem: 'Eliminar artículo',
      packComplete: 'Pack completo',
      photo: 'foto',
      photos: 'fotos',
      digitalFormat: 'Formato Digital',
      highResolution: 'Alta Resolución',
      errorEmailNameRequired: 'Introduce tu email y nombre para continuar',
      errorInvalidEmail: 'Introduce un email válido (ej: tu@email.com)',
      errorEmptyCart: 'El carrito está vacío',
      errorCheckoutSession: 'Error al crear la sesión de pago',
      errorNoPaymentUrl: 'No se recibió la URL de pago. Verifica que Stripe esté configurado.',
      errorPaymentFailed: 'Error al procesar el pago',
    },
    purchasePricingBar: {
      ariaLabel: 'Precios',
      disclaimer:
        'El descuento del segundo pack aplica cuando ambos packs se añaden juntos al carrito. Precios sin impuestos (IGIC).',
      blocks: [
        {
          description: 'Tu mejor foto',
          badge: '1 foto',
        },
        {
          description: 'Todas tus fotos de una sesión',
          badge: 'Pack completo',
        },
        {
          description: 'Sesión extra para ti',
          badge: '2.º pack',
        },
        {
          description: 'Todas las fotos de un amigo o familiar',
          badge: '2.º pack',
        },
      ],
    },
    // Partners
    partners: {
      title: 'Escuelas Colaboradoras',
    },
    // Footer
    footer: {
      brand: 'Surf Photo Lanzarote',
      description: 'Marketplace premium de fotografía de surf en Lanzarote',
      navigation: 'Navegación',
      home: 'Inicio',
      schools: 'Escuelas',
      howItWorks: 'Cómo Funciona',
      pricing: 'Precios',
      legal: 'Legal',
      legalNotice: 'Aviso Legal',
      privacy: 'Política de Privacidad',
      terms: 'Términos y Condiciones',
      contact: 'Contacto',
      email: 'info@surfphotolanzarote.com',
      location: 'Lanzarote, Islas Canarias',
      rights: 'Todos los derechos reservados',
    },
    // Common
    common: {
      loading: 'Cargando...',
      error: 'Ocurrió un error',
      close: 'Cerrar',
      cancel: 'Cancelar',
      confirm: 'Confirmar',
      save: 'Guardar',
      delete: 'Eliminar',
      edit: 'Editar',
      back: 'Volver',
    },
  };
